/**
 * useChargeFlow
 *
 * The state machine the money dialogs (listing publish / renew, agent-support add-on) share: one
 * charge in flight, the persisted idempotency key (lib/charge-idempotency), saved-card capture and
 * the classification of a failed charge into an outcome. The dialogs own only what differs: the
 * request, the codes specific to their route, and their copy.
 *
 * Key rules (BR-91 / BR-102), unchanged by being shared here:
 *  - the key is read just before the request and settled after it; a definitive outcome rotates it,
 *    an unresolved one (network error, 5xx, 429, CHARGE_IN_PROGRESS, unreconciled) keeps it;
 *  - a definitive failure or a consent mismatch re-reads the quote under the key now current.
 */

import { useRef, useState } from 'react';
import type { DefaultPaymentMethod } from '@resida/api-types/billing';
import { ApiClientError } from '../../lib/api.core';
import {
  getChargeIdempotencyKey,
  isRateLimitedError,
  isUnresolvedChargeError,
  RATE_LIMITED_MESSAGE,
  settleChargeIdempotencyKey,
} from '../../lib/charge-idempotency';
import { describeDecline, getPaymentErrorDetails, type BillingAddress, type PaymentErrorDetails } from '../../api/billing';
import { useConfirmPaymentMethodMutation, useCreatePaymentMethodSetupIntentMutation } from '../../hooks/useBilling';
import { useFreshSession } from '../../hooks/useFreshSession';

/** Outcomes every charge route can end in; a dialog adds the ones specific to its route. */
export type BaseChargeOutcome =
  | { kind: 'declined'; reason: string }
  | { kind: 'in_progress' }
  | { kind: 'unknown_outcome' }
  | { kind: 'unreconciled' }
  /** The price changed since the quote (consent mismatch); `feeCents` is the server's new fee when it sent one. */
  | { kind: 'price_changed'; feeCents: number | null }
  | { kind: 'error'; message: string };

export type ChargeOutcome<Extra = never> = BaseChargeOutcome | Extra;

interface ChargeFlowOptions<Extra> {
  /** Whether the dialog is open: the session-freshness probe runs on open, before any card form shows. */
  open: boolean;
  /** lib/charge-idempotency scope of the attempt (per listing + mode, or per subscription). */
  keyScope: string;
  onOpenChange: (open: boolean) => void;
  /** Re-read the quote on screen (after a consent mismatch or any definitive failure). */
  refetchQuote: () => void;
  /** The route's own "an earlier attempt is being confirmed" code (BR-91 / BR-102). */
  unreconciledCode: string;
  /** The saved card the quote reports, if any. */
  quotedCard: DefaultPaymentMethod | null | undefined;
  /**
   * Codes specific to the route: return the outcome to show, `'handled'` when the dialog dealt with
   * the error itself, or undefined to fall through to the shared classification.
   */
  classifyRouteError?: (err: ApiClientError, details: PaymentErrorDetails) => ChargeOutcome<Extra> | 'handled' | undefined;
}

export function useChargeFlow<Extra = never>({
  open,
  keyScope,
  onOpenChange,
  refetchQuote,
  unreconciledCode,
  quotedCard,
  classifyRouteError,
}: ChargeFlowOptions<Extra>) {
  // Save-card and charge are fresh-session-gated server-side: ask up front, so a stale session is
  // re-authenticated before the landlord fills in a card form rather than after they submit it.
  const sessionFresh = useFreshSession(open);
  const setupIntentMutation = useCreatePaymentMethodSetupIntentMutation();
  const confirmCardMutation = useConfirmPaymentMethodMutation();

  const [consent, setConsent] = useState(false);
  const [charging, setCharging] = useState(false);
  const [outcome, setOutcome] = useState<ChargeOutcome<Extra> | null>(null);
  const [capturingCard, setCapturingCard] = useState(false);
  const [savedCard, setSavedCard] = useState<DefaultPaymentMethod | null>(null);

  const chargingRef = useRef(false);

  const card = savedCard ?? quotedCard ?? null;

  const close = () => {
    if (!chargingRef.current) onOpenChange(false);
  };

  /** Dialog open-state handler that refuses to close while a charge is in flight. */
  const guardedOpenChange = (next: boolean) => {
    if (!next && chargingRef.current) return;
    onOpenChange(next);
  };

  /** Switch to card capture from the summary (the landlord chose another card); the outcome stays on screen. */
  const changeCard = () => setCapturingCard(true);

  /** Switch to card capture after a decline: the declined outcome is dismissed. */
  const startCardCapture = () => {
    setOutcome(null);
    setCapturingCard(true);
  };

  function classifyError(err: unknown) {
    if (!(err instanceof ApiClientError)) {
      // A network error / timeout: the charge may have landed, so say so (and retry under the same key).
      setOutcome({ kind: 'unknown_outcome' });
      return;
    }
    if (isRateLimitedError(err)) {
      // Rejected before any charge ran; the key is kept so a retry resumes the same attempt.
      setOutcome({ kind: 'error', message: RATE_LIMITED_MESSAGE });
      return;
    }
    const details = getPaymentErrorDetails(err);
    const routeOutcome = classifyRouteError?.(err, details);
    if (routeOutcome === 'handled') return;
    if (routeOutcome) {
      setOutcome(routeOutcome);
      return;
    }
    switch (err.code) {
      case 'PAYMENT_DECLINED':
        // A new attempt (typically on a different card) is a new charge: the key was rotated by the caller.
        setOutcome({ kind: 'declined', reason: describeDecline(details.declineCode) });
        return;
      case 'CHARGE_IN_PROGRESS':
        setOutcome({ kind: 'in_progress' });
        return;
      case 'CHARGE_OUTCOME_UNKNOWN':
        setOutcome({ kind: 'unknown_outcome' });
        return;
      case unreconciledCode:
        setOutcome({ kind: 'unreconciled' });
        return;
      case 'FEE_CONSENT_REQUIRED': {
        // The price changed since the quote: show the new one and require consent again.
        setConsent(false);
        const serverFee = details.quote?.feeCents;
        setOutcome({ kind: 'price_changed', feeCents: typeof serverFee === 'number' ? serverFee : null });
        return;
      }
      case 'PAYMENT_METHOD_REQUIRED':
        setSavedCard(null);
        setCapturingCard(true);
        return;
      default:
        if (isUnresolvedChargeError(err)) {
          setOutcome({ kind: 'unknown_outcome' });
          return;
        }
        setOutcome({ kind: 'error', message: err.message });
    }
  }

  /**
   * Run ONE charge: `request` receives the persisted idempotency key; on success the key is settled
   * and `onSuccess` runs; on failure the key is settled against the error and the error classified.
   */
  const charge = async <R>(request: (idempotencyKey: string) => Promise<R>, onSuccess: (result: R) => void) => {
    if (chargingRef.current) return;
    chargingRef.current = true;
    setCharging(true);
    setOutcome(null);
    try {
      const result = await request(getChargeIdempotencyKey(keyScope));
      settleChargeIdempotencyKey(keyScope);
      onSuccess(result);
    } catch (err) {
      settleChargeIdempotencyKey(keyScope, err);
      // A definitive outcome rotated the key (the next quote must not claim to resume the finished attempt) and a
      // consent mismatch means the quote on screen is stale: re-read it, under the key that is now current.
      if (err instanceof ApiClientError && (err.code === 'FEE_CONSENT_REQUIRED' || !isUnresolvedChargeError(err))) {
        refetchQuote();
      }
      classifyError(err);
    } finally {
      chargingRef.current = false;
      setCharging(false);
    }
  };

  /** Props for the shared card-capture step. */
  const cardCapture = {
    requestSetupIntent: (billingAddress: BillingAddress) => setupIntentMutation.mutateAsync(billingAddress),
    onConfirmed: async (setupIntentId: string) => {
      const confirmed = await confirmCardMutation.mutateAsync(setupIntentId);
      setSavedCard(confirmed.defaultPaymentMethod);
      setOutcome(null);
      setConsent(false);
      setCapturingCard(false);
    },
    onCancel: () => (card ? setCapturingCard(false) : close()),
  };

  return {
    /** False until the session is confirmed fresh; render the loading state meanwhile. */
    sessionFresh,
    consent,
    setConsent,
    charging,
    outcome,
    capturingCard,
    card,
    close,
    guardedOpenChange,
    changeCard,
    startCardCapture,
    charge,
    cardCapture,
    /** True while a charge is in flight (a ref: stable inside event handlers). */
    isChargeInFlight: () => chargingRef.current,
  };
}
